import {
  Search,
  MessageCircle,
  Bot,
  User,
  Send,
  Loader2,
  Sparkles,
  FileText,
  Building,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useBuildingStore } from "@/hooks/useBuildingStore";
import { usePermissions } from "@/hooks/usePermissions";
import { bindTrackToCurrentUser } from "@/lib/posthog";

interface SearchResult {
  type: "building" | "document";
  id: string;
  label: string;
  buildingId?: string;
}

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface ChatResultBuilding {
  id: string;
  name: string;
}

interface ChatResultDocument {
  id: string;
  filename: string;
  building_id: string;
}

interface ChatResults {
  buildings?: ChatResultBuilding[];
  documents?: ChatResultDocument[];
}

export function UnifiedSearchChat({ className }: { className?: string }) {
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearchMode, setIsSearchMode] = useState(true);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const permissions = usePermissions();
  const [chatResults, setChatResults] = useState<ChatResults | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { currentOrganization } = useOrganizationContext();
  const { t } = useLanguage();
  const buildingStore = useBuildingStore();

  // Detect if query is a question vs search term
  const isQuestion = (text: string) => {
    const questionWords = [
      "what",
      "how",
      "why",
      "when",
      "where",
      "who",
      "which",
      "can",
      "could",
      "would",
      "should",
      "is",
      "are",
      "do",
      "does",
      "did",
    ];
    const lowerText = text.toLowerCase().trim();
    return (
      lowerText.endsWith("?") ||
      questionWords.some((word) => lowerText.startsWith(word + " ")) ||
      lowerText.includes(" or ") ||
      lowerText.includes("tell me") ||
      lowerText.includes("show me") ||
      text.length > 50
    ); // Longer queries are likely questions
  };

  useEffect(() => {
    if (query.trim()) {
      setIsSearchMode(!isQuestion(query));
    } else {
      setIsSearchMode(true);
      setChatResults(null);
    }
  }, [query]);

  useEffect(() => {
    const fetchSearchResults = async () => {
      if (!query.trim() || !isSearchMode) {
        setSearchResults([]);
        return;
      }

      const buildingSummaries = await buildingStore.searchBuildings({
        orgId: currentOrganization?.id,
        query,
        limit: 5,
      });

      const documentsQuery = supabase
        .from("documents")
        .select("id, filename, building_id")
        .ilike("filename", `%${query}%`)
        .limit(5);

      if (currentOrganization?.id) {
        documentsQuery.eq("org_id", currentOrganization.id);
      }

      const { data: documentData, error: documentError } = await documentsQuery;
      if (documentError && process.env.NODE_ENV === "development") {
        console.error("Document search error", documentError);
      }

      const mappedBuildings: SearchResult[] = buildingSummaries.map((b) => ({
        type: "building",
        id: String(b.id),
        label: b.name,
      }));

      const mappedDocuments: SearchResult[] =
        documentData?.map((d) => ({
          type: "document",
          id: String(d.id),
          label: d.filename,
          buildingId: String(d.building_id),
        })) || [];

      setSearchResults([...mappedBuildings, ...mappedDocuments]);
    };

    const timeout = setTimeout(fetchSearchResults, 300);
    return () => clearTimeout(timeout);
  }, [query, currentOrganization?.id, isSearchMode, buildingStore]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setQuery("");
        setSearchResults([]);
        setChatResults(null);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setQuery("");
        setSearchResults([]);
        setChatResults(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSearchSelect = (result: SearchResult) => {
    setQuery("");
    setSearchResults([]);
    setChatResults(null);
    if (result.type === "building") {
      // Navigate to the Files page for non-admin users, building page for admins
      const destination = permissions.isAdmin
        ? `/buildings/${result.id}`
        : `/buildings/${result.id}/documents`;
      navigate(destination);
    } else {
      navigate(`/buildings/${result.buildingId}/detail`);
    }
  };

  const handleChatSubmit = async () => {
    if (!query.trim()) return;
    const track = bindTrackToCurrentUser(currentOrganization?.id);
    const exchangeId = crypto.randomUUID();

    const userMessage = { role: "user" as const, content: query };
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("unified-search-chat", {
        body: {
          prompt: query,
          orgId: currentOrganization?.id,
          mode: "chat",
        },
      });

      if (error) throw error;

      const assistantMessage = {
        role: "assistant" as const,
        content: data.response,
      };

      // This non-streaming endpoint confirms acceptance through its successful response.
      // The sidebar confirms it through the first accepted SSE event.
      track("assistant_message_sent", {
        surface: "unified_search",
        suggestion_used: false,
        attachment_count: 0,
        question_text: query,
        exchange_id: exchangeId,
      });
      track("assistant_response_received", {
        surface: "unified_search",
        question_text: query,
        answer_text: assistantMessage.content,
        exchange_id: exchangeId,
      });

      setMessages((prev) => [...prev, assistantMessage]);
      setChatResults(data.searchResults);
      setQuery("");
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Chat error:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = () => {
    if (isSearchMode && searchResults.length > 0) {
      handleSearchSelect(searchResults[0]);
    } else {
      handleChatSubmit();
    }
  };

  const hasResults =
    query.trim() && (searchResults.length > 0 || !isSearchMode || messages.length > 0);

  return (
    <div className={cn("relative", className)} ref={containerRef}>
      <div className="relative">
        <Input
          placeholder={isSearchMode ? t.common.search : "Ask about buildings or documents..."}
          aria-label={isSearchMode ? t.common.search : "Ask about buildings or documents"}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSubmit()}
          className="h-8 w-48 rounded-full border-gray-200 bg-white/90 pl-8 pr-12 text-sm backdrop-blur-sm placeholder:text-gray-400"
        />
        {isSearchMode ? (
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
        ) : (
          <MessageCircle className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-blue-500" />
        )}

        {query.trim() && (
          <Button
            onClick={handleSubmit}
            size="sm"
            className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full p-0"
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : isSearchMode ? (
              <Search className="h-3 w-3" />
            ) : (
              <Send className="h-3 w-3" />
            )}
          </Button>
        )}
      </div>

      {hasResults && (
        <div className="absolute z-50 mt-1 w-full max-w-md overflow-hidden rounded-lg border border-gray-100 bg-white shadow-lg">
          {isSearchMode && searchResults.length > 0 && (
            <div className="max-h-60 overflow-y-auto">
              <div className="flex items-center gap-2 border-b bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600">
                <Search className="h-3 w-3" />
                Quick Results
              </div>
              {searchResults.map((r, idx) => (
                <button
                  key={`${r.type}-${r.id}-${idx}`}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-accent"
                  onClick={() => handleSearchSelect(r)}
                >
                  {r.type === "building" ? (
                    <Building className="h-4 w-4 text-blue-500" />
                  ) : (
                    <FileText className="h-4 w-4 text-green-500" />
                  )}
                  <div>
                    <div className="text-sm font-medium">{r.label}</div>
                    <div className="text-xs text-muted-foreground">
                      {r.type === "building" ? "Building" : "Document"}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {!isSearchMode && (
            <div className="max-h-80 overflow-y-auto">
              <div className="flex items-center gap-2 border-b bg-blue-50 px-3 py-2 text-xs font-medium text-blue-600">
                <Sparkles className="h-3 w-3" />
                AI Assistant
              </div>

              {messages.length > 0 && (
                <div className="max-h-60 space-y-3 overflow-y-auto p-3">
                  {messages.slice(-3).map((message, index) => (
                    <div
                      key={index}
                      className={`flex gap-2 ${
                        message.role === "user" ? "justify-end" : "justify-start"
                      }`}
                    >
                      {message.role === "assistant" && (
                        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                          <Bot className="h-3 w-3 text-blue-600" />
                        </div>
                      )}
                      <div
                        className={`max-w-[80%] rounded-lg px-3 py-2 text-xs ${
                          message.role === "user"
                            ? "bg-blue-500 text-white"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {message.content}
                      </div>
                      {message.role === "user" && (
                        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-200">
                          <User className="h-3 w-3 text-gray-600" />
                        </div>
                      )}
                    </div>
                  ))}
                  {isLoading && (
                    <div className="flex justify-start gap-2">
                      <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                        <Loader2 className="h-3 w-3 animate-spin text-blue-600" />
                      </div>
                      <div className="rounded-lg bg-gray-100 px-3 py-2 text-xs">Thinking...</div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}

              {chatResults &&
                (chatResults.buildings?.length > 0 || chatResults.documents?.length > 0) && (
                  <div className="border-t p-3">
                    <div className="mb-2 text-xs font-medium text-gray-600">Related Items</div>
                    <div className="space-y-1">
                      {chatResults.buildings?.slice(0, 3).map((building: ChatResultBuilding) => (
                        <button
                          key={building.id}
                          onClick={() => navigate(`/buildings/${building.id}`)}
                          className="flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-gray-50"
                        >
                          <Building className="h-3 w-3 text-blue-500" />
                          <span className="truncate text-xs">{building.name}</span>
                        </button>
                      ))}
                      {chatResults.documents?.slice(0, 3).map((doc: ChatResultDocument) => (
                        <button
                          key={doc.id}
                          onClick={() => navigate(`/buildings/${doc.building_id}/detail`)}
                          className="flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-gray-50"
                        >
                          <FileText className="h-3 w-3 text-green-500" />
                          <span className="truncate text-xs">{doc.filename}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )}

          {isSearchMode && searchResults.length === 0 && query.trim() && (
            <div className="p-3 text-center text-sm text-muted-foreground">
              No results found
              <div className="mt-1">
                <button
                  onClick={() => setIsSearchMode(false)}
                  className="text-xs text-blue-500 hover:text-blue-600"
                >
                  Try asking AI instead
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
